import React, { FormEvent, useMemo, useState } from 'react'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'

import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  Button as MuiButton,
  Paper,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'

import { skuit } from 'src/services/skuit'

const Button = styled<any>(MuiButton)(spacing)

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)}px;

  ${(props) => props.theme.breakpoints.up('md')} {
    padding: ${(props) => props.theme.spacing(10)}px;
  }
`

const useQueryParams = () => {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}

export const ResetPassword = () => {
  const history = useHistory()
  const query = useQueryParams()
  const [matchError, setMatchError] = useState('')

  const token = query.get('token') || ''
  const { isLoading: tokenLoading, isError: isTokenError } = useQuery(
    ['validate-token', token],
    () => skuit.auth.validateResetToken({ token }),
    { retry: false },
  )

  const { mutate: resetPassword, isLoading, isError, error } = useMutation(skuit.auth.resetPassword, {
    onSuccess: () => {
      history.push('/auth/login')
    },
  })

  if (tokenLoading) {
    return <>Loading...</>
  }

  if (isTokenError) {
    return (
      <Wrapper>
        <Helmet title="Sign In" />

        <Typography component="h1" variant="h4" align="center">
          Reset Password
        </Typography>
        <Typography align="center">
          Reset Password token is either invalid or expired.
        </Typography>
        <Link to="/auth/forgot-password">Retry</Link>
      </Wrapper>
    )
  }

  const submitForm = (e: FormEvent) => {
    e.preventDefault()
    const data = new FormData(e.target as HTMLFormElement)
    const newPassword = data.get('newPassword')
    const confirmPassword = data.get('confirmPassword')
    if (newPassword !== confirmPassword) {
      setMatchError('Passwords do not match')
      return
    }
    setMatchError('')

    resetPassword({ password: newPassword as string, token: query.get('token') ?? '' })
  }

  return (
    <Wrapper>
      <Helmet title="Sign In" />

      <Typography component="h1" variant="h4" align="center">
        Reset Password
      </Typography>
      <form onSubmit={submitForm}>
        <FormControl margin="normal" required fullWidth error={!matchError && isError && error.response.data.password}>
          <InputLabel htmlFor="password">New Password</InputLabel>
          <Input
            name="newPassword"
            type="password"
            id="newPassword"
            autoComplete="current-password"
          />
          {(!matchError && isError) && (
            <FormHelperText>{error.response.data.password}</FormHelperText>
          )}
        </FormControl>

        <FormControl margin="normal" required fullWidth>
          <InputLabel htmlFor="password">Confirm Password</InputLabel>
          <Input
            name="confirmPassword"
            type="password"
            id="confirmPassword"
            autoComplete="current-password"
          />
        </FormControl>

        {matchError && <Typography color="error">{matchError}</Typography>}
        {isError && error.response.status !== 400 && <Typography color="error">{error.message}</Typography>}

        <Button
          type="submit"
          disabled={isLoading}
          fullWidth
          variant="contained"
          color="primary"
          mb={2}
        >
          Submit
        </Button>
      </form>
    </Wrapper>
  )
}
